import { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import { FeatureFlag } from '@ps/web-core-enums';
import { type Page, useFeature } from '@ps/web-core-store';
import { getScrollingElem } from '@ps/web-core-utils';
import type { SiteHeader } from '@ps/web-martech-cms-types';
import type { SecondaryNavigationBlock } from '@ps/web-martech-cms-types/SiteHeader';
import classNames from 'clsx';
import React, { useEffect } from 'react';
import BodyClassName from 'react-body-classname';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { CMSNavigation, CMSPage, CMSReferences, utils } from '@swf/cms';
import * as CMSContent from '@swf/cms';
import FloatingWelcomeOffer from '@swf/components/FloatingWelcomeOffer/FloatingWelcomeOffer';
import NPS from '@swf/components/NPS/NPS';
import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import ServiceUnavailable from '@swf/components/ServiceUnavailable/ServiceUnavailable';
import CasinoMaintenance from '@swf/components/ServiceUnavailable/components/CasinoMaintenance/CasinoMaintenance';
import useFirstRender from '@swf/core/hooks/useFirstRender';
import { select } from '@swf/core/store';

import styles from './StarsLayoutBase.scss';
import A2HS from './components/A2HS/A2HS';
import BrowserNotification from './components/BrowserNotification/BrowserNotification';
import SideMenu from './components/SideMenu/SideMenu';

const scrollingElement = getScrollingElem();

interface Props {
  showSidenav?: boolean;
  classes?: {
    cmsNav?: {
      root?: string;
      container?: string;
      nav?: string;
      navItem?: string;
    };
    sideNav?: string;
  };
}

const StarsLayoutBase: React.FC<Page & Props> = ({ showSidenav, classes, ...page }) => {
  const website = useSelector(select.cms.getWebsite);
  const { menu, depth } = useSelector(select.cms.getMenu);
  const location = useLocation();
  const isSystemAvailable = useSelector(select.system.isAvailable);
  const firstRender = useFirstRender();
  const isEmbedded = useSelector(select.device.isEmbedded);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const showWebContent = !(isEmbedded || embeddedRoute);
  const isSportsBrand = useSelector(select.cms.getBrandId) === BrandTypes.BET_STARS;
  const brandId = useSelector(select.cms.getBrandId);
  const cacheControl = useSelector(select.cms.getCacheControl);
  const isBrandFromList = (brands: BrandTypes[]) => brands.includes(brandId);
  const isCasinoServiceInterrupted =
    useFeature(FeatureFlag.CasinoServiceInterruptionSplashPage) &&
    brandId === BrandTypes.STARS_CASINO;

  const {
    uid,
    _content_type_uid,
    locale,
    content,
    template,
    theme,
    layout: {
      background_fit: backgroundFit,
      background_image: backgroundImage,
      background_color: backgroundColor
    }
  } = page;

  const { hide_navigation: hideNavigation } = useSelector(select.cms.getPage) || {};
  const showCMSNav =
    !hideNavigation &&
    menu &&
    (showWebContent || !isBrandFromList([BrandTypes.POKER_STARS, BrandTypes.SISAL]));

  let secondaryNavigation: SecondaryNavigationBlock[] = [];
  if (website?.site_header?.[0]) {
    const contentItem = CMSContent.useCMSReference<SiteHeader>(website?.site_header?.[0]);
    secondaryNavigation = utils.filterSecondaryNavItems(
      contentItem?.secondary_navigation || [],
      location.pathname
    );
  }

  useEffect(() => {
    if (!firstRender && scrollingElement && !location.hash) {
      scrollingElement.scrollTop = 0;
    }
  }, [firstRender, location]);

  const renderContent = () => {
    if (!isSystemAvailable || isCasinoServiceInterrupted) {
      return (
        <>
          <CacheControl>public, max-age=5</CacheControl>
          {isCasinoServiceInterrupted ? <CasinoMaintenance /> : <ServiceUnavailable />}
        </>
      );
    }

    return (
      <div
        className={classNames(styles.main, backgroundFit && styles[backgroundFit])}
        style={{
          ...(backgroundImage?.url && {
            backgroundImage: `url(${backgroundImage?.url})`
          }),
          ...(backgroundColor && { backgroundColor })
        }}
      >
        <CMSPage {...page} content={content} template={template} theme={theme} />
      </div>
    );
  };

  return (
    <>
      {cacheControl && <CacheControl>{cacheControl}</CacheControl>}
      <Helmet
        bodyAttributes={{
          'data-pageref': uid,
          'data-contenttype': _content_type_uid,
          'data-locale': locale
        }}
      >
        <html className={styles.html} />
      </Helmet>
      <BodyClassName className={styles.body} />
      {showWebContent && <BrowserNotification />}
      {!isEmbedded && <A2HS />}
      {showWebContent && <CMSReferences references={website?.site_header} />}

      <section
        className={classNames(styles.contentSection, {
          [styles.withSidenav]: showSidenav
        })}
      >
        {showSidenav && <SideMenu className={classes?.sideNav} items={secondaryNavigation} />}
        <div className={styles.mainContent}>
          {showCMSNav && (
            <CMSNavigation classes={classes?.cmsNav} menu={menu} maxDepth={depth || 1} />
          )}

          {renderContent()}

          {(showWebContent || isSportsBrand) && (
            <>
              <CMSReferences references={website?.site_footer} />
              <FloatingWelcomeOffer />
              <NPS />
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default StarsLayoutBase;
